import { useTranslation } from 'react-i18next';
import { useFetchServicePreferences } from '@/hooksApi/useServicePreference';
import type { ServiceResolution } from '@/types';

/**
 * BOXY-588 — an address edit can leave the order's service unable to reach the
 * new address (Boxy Save → a remote area). The resolve answer, asked with the
 * edited region, already lists every service and whether it can reach it.
 *
 * Returns `check(code)`: undefined while the service still ships here (or the
 * answer is unknown), otherwise the notice for the service step plus the
 * service to fall back to (the backend's own pick, when it can ship here).
 */
const useBlockedServiceNotice = (resolution: ServiceResolution | undefined) => {
  const { t } = useTranslation();
  const { data } = useFetchServicePreferences();

  return (code: string | null | undefined) => {
    const services = resolution?.services ?? [];
    if (!code || !services.some((s) => s.code === code && !s.available)) return undefined;

    const service = data?.services?.find((s) => s.code === code)?.title ?? code;
    const fallback = services.some((s) => s.code === resolution?.service_code && s.available)
      ? (resolution?.service_code ?? undefined)
      : undefined;
    return { notice: String(t('service_preference_not_offered', { service })), fallback };
  };
};

export default useBlockedServiceNotice;
